import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './contexts';
import { ProjectProvider } from './contexts/ProjectContext';
import GlobalStyles from './styles/GlobalStyles';
import theme from './styles/theme';

// Import the theme type from styled-components
import { DefaultTheme } from 'styled-components';
import Layout from './components/Layout';
import Navigation from './components/Navigation';
import SplashScreen from './components/SplashScreen';
import LoginPage from './pages/LoginPage';
import InstallPage from './pages/InstallPage';
import DashboardPage from './pages/DashboardPage';
import CartonsPage from './pages/CartonsPage';
import CartonReceivingPage from './pages/CartonReceivingPage';
import CartonContentReceivingPage from './pages/CartonContentReceivingPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import CartonDispatchPage from './pages/CartonDispatchPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import CartonDetailPage from './pages/CartonDetailPage';
import ProfilePage from './pages/ProfilePage';
import { LoadingSpinner } from './components/LoadingSpinner';
import styled from 'styled-components';
import { UserProfile } from './types/navigation';
import { appUpdateService } from './services/appUpdateService';
import SmartPWAInstall from './components/SmartPWAInstall';
import SyncStatusIndicator from './components/SyncStatusIndicator';

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  transition: opacity 0.3s ease-in-out;
`;

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (!user) {
      return <Navigate to="/login" />;
    }

    const userProfile: UserProfile = {
      name: user.email?.split('@')[0] || 'User',
      role: user.role || 'User'
    };

    return (
      <>
        <Navigation user={userProfile} />
        <Layout>{children}</Layout>
      </>
    );
  };

  // Only show loading on initial load
  if (loading && !user) {
    return (
      <LoadingOverlay>
        <LoadingSpinner size="large" />
      </LoadingOverlay>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/install"
        element={<InstallPage />}
      />
      <Route
        path="/login"
        element={
          user ? (
            <Navigate to="/dashboard" />
          ) : (
            <LoginPage />
          )
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Project routes */}
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <ProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute>
            <ProjectDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Carton routes - Reordered to prevent conflicts */}
      <Route
        path="/cartons/dispatch"
        element={
          <ProtectedRoute>
            <CartonDispatchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cartons/receiving"
        element={
          <ProtectedRoute>
            <CartonReceivingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cartons/content"
        element={
          <ProtectedRoute>
            <CartonContentReceivingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cartons/:id/dispatch"
        element={
          <ProtectedRoute>
            <CartonDispatchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cartons/:id/receive"
        element={
          <ProtectedRoute>
            <CartonReceivingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cartons/:id/content"
        element={
          <ProtectedRoute>
            <CartonContentReceivingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cartons/:id"
        element={
          <ProtectedRoute>
            <CartonDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cartons"
        element={
          <ProtectedRoute>
            <CartonsPage />
          </ProtectedRoute>
        }
      />

      {/* Other protected routes */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all route - 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};


// Add this hook in your App component or create a separate hook file
const useErrorSuppression = () => {
  useEffect(() => {
    // Suppress ResizeObserver errors in console
    const originalError = console.error;
    console.error = (...args) => {
      if (
        typeof args[0] === 'string' &&
        args[0].includes('ResizeObserver loop completed with undelivered notifications')
      ) {
        return; // Suppress this specific error
      }
      originalError.apply(console, args);
    };

    // Handle window errors
    const handleError = (event: ErrorEvent) => {
      if (event.message?.includes('ResizeObserver loop')) {
        event.preventDefault();
        event.stopPropagation();
        console.warn('ResizeObserver loop detected and suppressed');
        return false;
      }
    };

    // Handle promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.message?.includes('ResizeObserver loop')) {
        event.preventDefault();
        console.warn('ResizeObserver promise rejection suppressed');
        return false;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      console.error = originalError; // Restore original console.error
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
};



const App: React.FC = () => {
  // Initialize error suppression
  useErrorSuppression();
  
  // Initialize app update service
  useEffect(() => {
    // Get the current app version from package.json or environment variable
    const appVersion = process.env.REACT_APP_VERSION || '1.0.0';
    appUpdateService.initialize(appVersion);
    
    // Clean up on unmount
    return () => {
      appUpdateService.destroy();
    };
  }, []);

  // Use the theme directly with type assertion
  const appTheme = theme as unknown as DefaultTheme;

  return (
    <ThemeProvider theme={appTheme}>
      <ProjectProvider>
        <GlobalStyles />
        <SplashScreen />
        <AppRoutes />
            <SmartPWAInstall />
            <SyncStatusIndicator />
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              borderRadius: '4px',
              background: theme.colors.background,
              color: theme.colors.text,
              boxShadow: theme.shadows.md,
            },
          }}
        />
      </ProjectProvider>
    </ThemeProvider>
  );
};

export default App;